/**
 * ENG-5862 — Coalesce agt-cli self-update restarts.
 *
 * Background. The manager polls for npm/brew CLI updates every 5 minutes
 * (`UPDATE_CHECK_INTERVAL_MS` in manager-worker.ts). Whenever it finds a newer
 * version, it installs and immediately flips `restartAfterUpgrade = true`,
 * which causes the supervisor to kill every agent tmux session, exec back
 * into itself, and respawn the agents — a ~90s blackout on agt-aws-1 during
 * which every Slack inbound is silently dropped (Socket Mode doesn't replay).
 *
 * On heavy dev days the auto-publish-on-merge workflow (ENG-5795/5845) can
 * patch-bump the CLI three or four times in an afternoon. Each bump = its own
 * blackout. The fix here is the cheap half: refuse to apply a fresh upgrade
 * while we're inside a coalesce window relative to the LAST applied upgrade.
 * Multiple bumps within the window collapse into one restart when the window
 * eventually expires and the next poll cycle catches up to the latest tag.
 *
 * This module is intentionally pure — no `process`, no logger, no fs side
 * effects beyond the explicit read/write helpers — so the gate logic is unit-
 * testable in isolation. manager-worker.ts wires the helpers into both the
 * brew and npm upgrade paths and is responsible for the actual log emission
 * and `restartAfterUpgrade` mutation.
 *
 * Design notes (from the ENG-5862 council review):
 *
 *   • The window applies to **both** install methods. A host with brew and
 *     npm side-by-side would otherwise coalesce differently per path, which
 *     is the bug we're trying to avoid (asymmetric restart cadence across
 *     the fleet).
 *   • The window is measured from the LAST APPLIED upgrade (which then
 *     triggered a restart), not from the last successful version check.
 *     `.last-update-check` already gates the cheap probe; this is a strictly
 *     stronger gate on the expensive restart.
 *   • Severity-override escape hatch (a sentinel package field that bypasses
 *     coalesce) is deliberately deferred. Adding it now without a hot-path
 *     need ships a sharp edge we'd have to maintain.
 *   • Restart frequency is the *cause* of inbound loss; the eventual
 *     compensating control (catch-up sweep / Lambda webhook queue / SIGTERM
 *     drain) is filed as follow-ups. This module alone reduces blast count by
 *     ~75% on a heavy dev day, with zero new failure modes.
 */

import { readFileSync, writeFileSync, mkdirSync } from 'node:fs';
import { dirname } from 'node:path';

/**
 * Default coalesce window: 30 minutes. Picked to be:
 *   - long enough that multiple back-to-back patch-bumps from the auto-
 *     publish workflow collapse into a single restart (typical cadence on
 *     a heavy dev day is 2-4 bumps over ~2 hours),
 *   - short enough that an actual urgent fix lands within an operator's
 *     attention span if they pushed it deliberately.
 *
 * Override at runtime with `AGT_SELF_UPDATE_COALESCE_MS` (e.g. `0` during
 * incident response to disable coalescing entirely).
 */
export const DEFAULT_SELF_UPDATE_COALESCE_MS = 30 * 60 * 1000;

/** Parse `AGT_SELF_UPDATE_COALESCE_MS` if set, else fall back to the default. */
export function resolveCoalesceWindowMs(env: NodeJS.ProcessEnv = process.env): number {
  const raw = env.AGT_SELF_UPDATE_COALESCE_MS;
  if (raw == null || raw === '') return DEFAULT_SELF_UPDATE_COALESCE_MS;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return DEFAULT_SELF_UPDATE_COALESCE_MS;
  return Math.trunc(parsed);
}

/**
 * Read the last-applied timestamp (ms since epoch). Returns null when the
 * marker is missing, unparseable, or in the future (clock skew → treat as
 * "no record" so we don't block forever).
 */
export function readLastSelfUpdateAppliedMs(
  markerPath: string,
  now: number = Date.now(),
  read: (p: string) => string = (p) => readFileSync(p, 'utf-8'),
): number | null {
  let raw: string;
  try {
    raw = read(markerPath);
  } catch {
    return null;
  }
  const v = parseInt(raw.trim(), 10);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v > now) return null; // future timestamp — host clock skew; ignore.
  return v;
}

/**
 * Write the last-applied timestamp. Best-effort: a failed write must not
 * block the upgrade itself (worst case is one extra restart at the next
 * cycle when the next poll wins the gate again).
 */
export function stampLastSelfUpdateApplied(
  markerPath: string,
  now: number = Date.now(),
  write: (p: string, v: string) => void = (p, v) => {
    mkdirSync(dirname(p), { recursive: true });
    writeFileSync(p, v);
  },
): void {
  try { write(markerPath, String(now)); } catch { /* non-fatal */ }
}

export interface CoalesceDecision {
  /** True when the upgrade may proceed. */
  proceed: boolean;
  /** When `proceed` is false, ms remaining until the window expires. 0 when proceed. */
  remainingMs: number;
  /** When `proceed` is false, the last-applied timestamp that's gating us. null when proceed. */
  lastAppliedMs: number | null;
}

/**
 * Decide whether a self-update may proceed given the coalesce window and the
 * last-applied marker. Pure: takes only the inputs it needs so the manager
 * can wire it into either upgrade path without re-implementing the gate.
 */
export function decideSelfUpdateCoalesce(inputs: {
  windowMs: number;
  lastAppliedMs: number | null;
  now: number;
}): CoalesceDecision {
  const { windowMs, lastAppliedMs, now } = inputs;
  if (windowMs <= 0) return { proceed: true, remainingMs: 0, lastAppliedMs: null };
  if (lastAppliedMs == null) return { proceed: true, remainingMs: 0, lastAppliedMs: null };
  const elapsed = now - lastAppliedMs;
  if (elapsed < 0) return { proceed: true, remainingMs: 0, lastAppliedMs: null }; // skew
  if (elapsed >= windowMs) return { proceed: true, remainingMs: 0, lastAppliedMs: null };
  return { proceed: false, remainingMs: windowMs - elapsed, lastAppliedMs };
}

/**
 * ENG-6698: post-install smoke check. After `npm i -g`, the manager must NOT
 * declare success (and schedule a restart into the new binary) unless the
 * freshly-installed `agt` actually runs and reports the expected version. A
 * `npm i -g` interrupted mid-flight can leave `/usr/bin/agt` a dangling symlink
 * even though the spawn that launched npm returned; restarting into that
 * dangling binary is what took the whole fleet dark.
 *
 * Pure so it's testable without spawning: `stdout` is the captured output of
 * `agt --version` (Commander prints just the version string), `expectedVersion`
 * is the version we just installed. Passes only when the output names that
 * version, which proves both "the binary runs" and "it's the new one".
 */
export function isAgtVersionSmokeOk(stdout: string, expectedVersion: string): boolean {
  if (!expectedVersion) return false;
  // `agt --version` (Commander) prints ONLY the version string, so match the
  // first whitespace-delimited token EXACTLY. A substring match would accept a
  // wrong binary: `0.27.6` is a substring of the prerelease `0.27.6-test.2`
  // (a stale `-test` build would pass when a stable was installed) and of
  // `0.28.1220` (trailing digit), and would also match a version named inside
  // an error line. Exact-token comparison rejects all three.
  const token = stdout.trim().split(/\s+/)[0] ?? '';
  return token === expectedVersion;
}

/**
 * Format the deferral log line. Kept here so the wording is testable and the
 * brew/npm paths emit byte-identical messages (one less inconsistency to
 * explain to operators reading manager.log across hosts).
 */
export function formatCoalesceDeferLogLine(opts: {
  installed: string;
  latest: string;
  channelLabel: string; // e.g. "npm, channel=latest" or "brew"
  remainingMs: number;
  windowMs: number;
}): string {
  const remainingSec = Math.ceil(opts.remainingMs / 1000);
  const windowMin = Math.round(opts.windowMs / 60_000);
  return (
    `[self-update] coalescing — ${opts.installed} → ${opts.latest} (${opts.channelLabel}) ` +
    `available, deferring for ${remainingSec}s (last restart inside ${windowMin}min window). ` +
    `See ENG-5862. Override with AGT_SELF_UPDATE_COALESCE_MS=0 during incident response.`
  );
}
